<!-- eslint-disable vue/no-mutating-props -->
<!-- 管理员、角色、系统参数与支付配置的弹窗模块。表单状态由父页面集中管理。 -->
<template>
  <el-dialog
    :model-value="adminEditorVisible"
    :title="isEditingAdmin ? '编辑管理员' : '新增管理员'"
    width="720px"
    @update:model-value="emit('update:adminEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="登录账号" hint="管理员登录后台时使用的账号。" required>
            <el-input v-model.trim="adminForm.account" placeholder="如 admin" />
          </GuidedField>
          <GuidedField label="管理员昵称" hint="展示在后台右上角和操作日志中。">
            <el-input v-model.trim="adminForm.nickname" placeholder="如 张三" />
          </GuidedField>
          <GuidedField label="登录密码" :hint="isEditingAdmin ? '留空表示保留原密码，不修改。' : '首次创建管理员时必须设置。'" :required="!isEditingAdmin">
            <el-input v-model.trim="adminForm.password" type="password" show-password :placeholder="isEditingAdmin ? '留空不修改' : '请输入登录密码'" />
          </GuidedField>
          <GuidedField label="角色" hint="角色决定管理员可以访问哪些功能。" required>
            <el-select v-model="adminForm.roleIds" multiple @change="emit('adminRoleIdsTouched')">
              <el-option v-for="role in systemRoles" :key="role.id" :label="role.name" :value="role.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="管理门店" hint="可多选门店；不选择表示可以管理全部门店。" wide>
            <el-select v-model="adminForm.storeIds" multiple filterable placeholder="全部门店">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="账号状态" hint="停用后该管理员无法登录后台。">
            <el-select v-model="adminForm.status">
              <el-option label="启用" value="ENABLED" />
              <el-option label="停用" value="DISABLED" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:adminEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="adminSubmitting" :disabled="!adminForm.account" @click="emit('submitAdmin')">
        {{ isEditingAdmin ? '保存修改' : '创建管理员' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="roleEditorVisible"
    :title="isEditingRole ? '编辑角色' : '新增角色'"
    width="720px"
    @update:model-value="emit('update:roleEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="角色名称" hint="展示在管理员配置和权限列表中。" required>
            <el-input v-model.trim="roleForm.name" placeholder="如 门店店长" />
          </GuidedField>
          <GuidedField label="角色编码" hint="系统内部识别角色的唯一编码，建议使用英文大写。">
            <el-input v-model.trim="roleForm.code" placeholder="如 STORE_MANAGER" />
          </GuidedField>
          <GuidedField label="数据范围" hint="限制该角色能查看和操作的数据范围。">
            <el-select v-model="roleForm.dataScope">
              <el-option label="全部数据" value="ALL" />
              <el-option label="所属门店" value="STORE" />
              <el-option label="仅本人" value="SELF" />
            </el-select>
          </GuidedField>
          <GuidedField label="角色状态" hint="停用后拥有该角色的管理员将无法使用对应权限。">
            <el-select v-model="roleForm.status">
              <el-option label="启用" value="ENABLED" />
              <el-option label="停用" value="DISABLED" />
            </el-select>
          </GuidedField>
          <GuidedField label="权限" hint="勾选该角色允许使用的后台功能权限。" wide required>
            <el-checkbox-group v-model="roleForm.permissions" class="permission-options" @change="handlePermissionChange">
              <el-checkbox
                v-for="option in mergedPermissionOptions"
                :key="option.value"
                :value="option.value"
                :disabled="isAllPermissionSelected && option.value !== Permission.All"
              >
                <span class="permission-option__label">{{ option.label }}</span>
              </el-checkbox>
            </el-checkbox-group>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:roleEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="roleSubmitting" :disabled="!roleForm.name" @click="emit('submitRole')">
        {{ isEditingRole ? '保存修改' : '创建角色' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="systemParamEditorVisible"
    :title="isEditingSystemParam ? '编辑系统参数' : '新增系统参数'"
    width="680px"
    @update:model-value="emit('update:systemParamEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="参数编码" hint="系统内部读取参数时使用；编辑时不可修改。" required>
            <el-input v-model.trim="systemParamForm.key" :disabled="isEditingSystemParam" placeholder="如 order.cancel_timeout" />
          </GuidedField>
          <GuidedField label="参数分组" hint="用于后台归类管理，选填。">
            <el-input v-model.trim="systemParamForm.groupName" placeholder="如 订单配置" />
          </GuidedField>
          <GuidedField label="参数值" hint="可填写普通文本或 JSON，具体格式取决于参数编码。" wide required>
            <el-input v-model="systemParamForm.value" type="textarea" :rows="4" placeholder='如 true 或 {"enabled":true}' />
          </GuidedField>
          <GuidedField label="参数说明" hint="说明这个参数影响什么功能，方便后续维护。" wide>
            <el-input v-model.trim="systemParamForm.remark" placeholder="如 订单自动取消时间" />
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:systemParamEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="systemParamsSubmitting" :disabled="!systemParamForm.key" @click="emit('submitSystemParam')">
        {{ isEditingSystemParam ? '保存修改' : '创建参数' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="paymentConfigEditorVisible"
    :title="isEditingPaymentConfig ? '编辑支付配置' : '新增支付配置'"
    width="760px"
    @update:model-value="emit('update:paymentConfigEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="支付渠道编码" hint="支付服务的内部编码，例如 WECHAT。" required>
            <el-input v-model.trim="paymentConfigForm.channel" placeholder="如 WECHAT" />
          </GuidedField>
          <GuidedField label="应用 ID" hint="微信小程序或应用的 AppID；编辑时留空表示不修改。">
            <el-input v-model.trim="paymentConfigForm.appId" placeholder="请输入 AppID" />
          </GuidedField>
          <GuidedField label="商户号" hint="支付平台分配的商户编号；编辑时留空表示不修改。">
            <el-input v-model.trim="paymentConfigForm.mchId" placeholder="请输入商户号" />
          </GuidedField>
          <GuidedField label="支付回调地址" hint="支付完成后，支付平台通知后台的接口地址。" wide>
            <el-input v-model.trim="paymentConfigForm.notifyUrl" placeholder="https://example.com/payment/notify" />
          </GuidedField>
          <GuidedField label="API 密钥" hint="敏感信息，仅在新增或需要更新时填写。">
            <el-input v-model.trim="paymentConfigForm.apiKey" type="password" show-password placeholder="留空不修改" />
          </GuidedField>
          <GuidedField label="API V3 密钥" hint="微信支付 V3 使用的敏感密钥，仅在需要更新时填写。">
            <el-input v-model.trim="paymentConfigForm.apiV3Key" type="password" show-password placeholder="留空不修改" />
          </GuidedField>
          <GuidedField label="配置状态" hint="停用后该支付渠道不会被用于新订单。">
            <el-select v-model="paymentConfigForm.enabled">
              <el-option label="启用" :value="true" />
              <el-option label="停用" :value="false" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:paymentConfigEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="paymentConfigSubmitting" :disabled="!paymentConfigForm.channel" @click="emit('submitPaymentConfig')">
        {{ isEditingPaymentConfig ? '保存修改' : '创建配置' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Permission } from '@jiaofa/constants'
import GuidedField from '@/components/GuidedField.vue'
import { expandImplicitPermissions } from '@/utils/permissions'
import type { AdminStore } from '@/api/store'
import type {
  AdminRole,
  AdminSavePayload,
  PaymentConfigSavePayload,
  RoleSavePayload,
  SystemParamSavePayload,
} from '@/api/system'

const props = defineProps<{
  adminEditorVisible: boolean
  roleEditorVisible: boolean
  systemParamEditorVisible: boolean
  paymentConfigEditorVisible: boolean
  adminSubmitting: boolean
  roleSubmitting: boolean
  systemParamsSubmitting: boolean
  paymentConfigSubmitting: boolean
  adminForm: AdminSavePayload
  roleForm: RoleSavePayload
  systemParamForm: SystemParamSavePayload
  paymentConfigForm: PaymentConfigSavePayload
  systemRoles: AdminRole[]
  storeOptions: AdminStore[]
}>()

const emit = defineEmits<{
  'update:adminEditorVisible': [value: boolean]
  'update:roleEditorVisible': [value: boolean]
  'update:systemParamEditorVisible': [value: boolean]
  'update:paymentConfigEditorVisible': [value: boolean]
  adminRoleIdsTouched: []
  submitAdmin: []
  submitRole: []
  submitSystemParam: []
  submitPaymentConfig: []
}>()

const isEditingAdmin = computed(() => Boolean(props.adminForm.id))
const isEditingRole = computed(() => Boolean(props.roleForm.id))
const isEditingSystemParam = computed(() => Boolean(props.systemParamForm.id))
const isEditingPaymentConfig = computed(() => Boolean(props.paymentConfigForm.id))

interface PermissionOption {
  label: string
  value: string
}

const permissionOptions: PermissionOption[] = [
  { label: '全部权限', value: Permission.All },
  { label: '工作台查看', value: Permission.DashboardView },
  { label: '工作台操作', value: Permission.DashboardEdit },
  { label: '订单查看', value: Permission.OrderView },
  { label: '订单操作', value: Permission.OrderEdit },
  { label: '退款查看', value: Permission.RefundView },
  { label: '退款操作', value: Permission.RefundEdit },
  { label: '门店查看', value: Permission.StoreView },
  { label: '门店操作', value: Permission.StoreEdit },
  { label: '商品查看', value: Permission.ProductView },
  { label: '商品操作', value: Permission.ProductEdit },
  { label: '配送查看', value: Permission.DeliveryView },
  { label: '配送操作', value: Permission.DeliveryEdit },
  { label: '无人机查看', value: Permission.DroneView },
  { label: '无人机操作', value: Permission.DroneEdit },
  { label: '用户查看', value: Permission.UserView },
  { label: '用户操作', value: Permission.UserEdit },
  { label: '会员查看', value: Permission.MemberView },
  { label: '会员操作', value: Permission.MemberEdit },
  { label: '营销查看', value: Permission.MarketingView },
  { label: '营销操作', value: Permission.MarketingEdit },
  { label: '数据查看', value: Permission.DataView },
  { label: '数据操作', value: Permission.DataEdit },
  { label: '财务查看', value: Permission.FinanceView },
  { label: '财务操作', value: Permission.FinanceEdit },
  { label: '消息查看', value: Permission.MessageView },
  { label: '消息操作', value: Permission.MessageEdit },
  { label: '系统查看', value: Permission.SystemView },
  { label: '系统操作', value: Permission.SystemEdit },
  { label: '角色查看', value: Permission.SystemRoleView },
  { label: '角色操作', value: Permission.SystemRoleEdit },
]

const mergedPermissionOptions = computed(() => {
  const selectedPermissions = props.roleForm.permissions ?? []
  const optionValues = new Set(permissionOptions.map((option) => option.value))
  const customOptions = selectedPermissions
    .filter((permission) => permission && !optionValues.has(permission))
    .map((permission) => ({ label: '自定义权限', value: permission }))

  return [...permissionOptions, ...customOptions]
})

const isAllPermissionSelected = computed(() => (props.roleForm.permissions ?? []).includes(Permission.All))

function handlePermissionChange(value: unknown[]): void {
  const stringValues = value.map(String)
  updateRoleFormPermissions(props.roleForm, stringValues.includes(Permission.All)
    ? [Permission.All]
    : expandImplicitPermissions(stringValues))
}

function updateRoleFormPermissions(roleForm: RoleSavePayload, permissions: string[]): void {
  Object.assign(roleForm, {
    permissions,
  })
}
</script>

<style scoped lang="scss">
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.detail-grid--single { grid-template-columns: 1fr; }
.detail-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; }
.detail-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.permission-options { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; }
.permission-options :deep(.el-checkbox) { height: auto; margin-right: 0; white-space: normal; }
.permission-option__label { color: var(--jf-color-text-primary); }
@media (max-width: 760px) {
  .detail-grid, .detail-actions, .permission-options { grid-template-columns: 1fr; }
}
</style>
